"use strict";

Object.defineProperty(exports, "__esModule", {
  value: true
});
exports.cancelVerify = cancelVerify;
exports.confirmVerify = confirmVerify;
exports.countVerifyRecords = countVerifyRecords;
exports.createDynamicCode = createDynamicCode;
exports.getMerchantAccount = getMerchantAccount;
exports.getMerchantBills = getMerchantBills;
exports.getMerchantLedger = getMerchantLedger;
exports.getMerchantOverview = getMerchantOverview;
exports.getShareRelation = getShareRelation;
exports.getStaticCode = getStaticCode;
exports.getVerifyRecords = getVerifyRecords;
exports.newRequestId = newRequestId;
exports.saveShareRelation = saveShareRelation;
var _request = _interopRequireDefault(require("@/utils/request"));
function _interopRequireDefault(e) { return e && e.__esModule ? e : { default: e }; }
/**
 * 校园收益联盟 API（商户端）
 *
 * 后端在 tjMall-alliance 模块，经网关 /alliance 路由转发（StripPrefix=1），
 * 所以 url 要带上后端自己的 /api 前缀。
 *
 * 鉴权沿用商户端现有登录态：request.js 会带上 `Authori-zation` 头，
 * 联盟侧的 MallTokenResolver 认得商户管理员 / 店铺管理员 / 服务店员三种令牌。
 *
 * ⚠️ 所有接口都不传 merchantId：后端一律以令牌为准（`ScopeGuard.resolveMerchantId`），
 * 传了也会被覆盖 —— 这是防止替别家店确认核销的那道校验，前端不要试图绕。
 */

// 没配 VUE_APP_ALLIANCE_API 时按主站地址推导，避免像 flowable 那样留一个写死的 IP。
// ⚠️ 推导只在 VUE_APP_BASE_API 是纯网关地址时才对：生产环境那个值末尾带 /admin，
// 推导会得到 .../admin/alliance，所以各环境都应显式配 VUE_APP_ALLIANCE_API
var baseURL = process.env.VUE_APP_ALLIANCE_API || "".concat(process.env.VUE_APP_BASE_API || location.origin, "/alliance");

/*
 * 两个变量都没取到时会 fallback 到 location.origin，请求打到前端自己身上，
 * 表现为一片莫名其妙的 404。改完 .env 必须重启 dev server ——
 * vue-cli 只在启动时读一次 .env，热更新不会重读。
 */
if (!process.env.VUE_APP_ALLIANCE_API && !process.env.VUE_APP_BASE_API) {
  // eslint-disable-next-line no-console
  console.error('[alliance] 未配置 VUE_APP_ALLIANCE_API，联盟接口会打到前端自身导致 404。' + '请在 .env.development 配置后重启 dev server。当前 baseURL =', baseURL);
}

/** 客户端幂等键。确认与撤销都要求带 requestId（PRD 8.7），重复提交只生效一次 */
function newRequestId() {
  var rand = Math.random().toString(36).slice(2, 10);
  return "mer".concat(Date.now()).concat(rand);
}

// ===================== 商户码 =====================

/** 固定商户码 / 门店码，长期有效；金额由用户输入，需商户二次确认 */
function getStaticCode(storeId) {
  return (0, _request.default)({
    url: '/api/merchant/code/static',
    method: 'get',
    params: {
      storeId: storeId
    },
    baseURL: baseURL
  });
}

/** 生成动态订单码，60 秒过期，用户扫完直接核销不需二次确认。amount 单位为分 */
function createDynamicCode(bizNo, amount, storeId) {
  return (0, _request.default)({
    url: '/api/merchant/code/dynamic',
    method: 'post',
    params: {
      bizNo: bizNo,
      amount: amount,
      storeId: storeId
    },
    baseURL: baseURL
  });
}

// ===================== 核销 =====================

/** 确认核销：扣积分、记应收、投递计佣事件。这是唯一真正扣用户积分的入口 */
function confirmVerify(data) {
  return (0, _request.default)({
    url: '/api/merchant/verify/confirm',
    method: 'post',
    data: data,
    baseURL: baseURL
  });
}

/** 撤销核销。refundPoints 为空表示全额撤销，填了则按比例部分冲正 */
function cancelVerify(data) {
  return (0, _request.default)({
    url: '/api/merchant/verify/cancel',
    method: 'post',
    data: data,
    baseURL: baseURL
  });
}

/** 本店核销记录。返回的是数组，总数要另外调 countVerifyRecords */
function getVerifyRecords(params) {
  return (0, _request.default)({
    url: '/api/merchant/verify/records',
    method: 'get',
    params: params,
    baseURL: baseURL
  });
}

/** 本店核销记录总数，配合分页使用 */
function countVerifyRecords(status) {
  return (0, _request.default)({
    url: '/api/merchant/verify/count',
    method: 'get',
    params: {
      status: status
    },
    baseURL: baseURL
  });
}

// ===================== 账户 =====================

/** 商户结算账户与抵扣规则（各档余额、服务费率、抵扣上限） */
function getMerchantAccount() {
  return (0, _request.default)({
    url: '/api/merchant/account',
    method: 'get',
    baseURL: baseURL
  });
}

/** 商户资金流水 */
function getMerchantLedger() {
  var page = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : 1;
  var size = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : 20;
  return (0, _request.default)({
    url: '/api/merchant/account/ledger',
    method: 'get',
    params: {
      page: page,
      size: size
    },
    baseURL: baseURL
  });
}

// ===================== 经营概览与账单 =====================

/**
 * 经营概览：今日 / 本月核销汇总 + 待确认笔数 + 账户四档余额。
 *
 * 口径与核销记录页完全一致（同一张表、只统计 VERIFIED 与 PART_REFUND），
 * 两边对不上时是筛选条件不同，不是两套账。金额单位是分。
 */
function getMerchantOverview() {
  return (0, _request.default)({
    url: '/api/merchant/overview',
    method: 'get',
    baseURL: baseURL
  });
}

/**
 * 账单中心：按日或按月聚合。granularity 只接受 'DAY' / 'MONTH'，
 * 传别的后端会直接拒绝而不是悄悄按日返回 —— 页面写着「月账单」却给日数据最难查。
 *
 * 不传时间时后端给默认区间：日账单近 90 天、月账单近 24 个月。
 */
function getMerchantBills(params) {
  return (0, _request.default)({
    url: '/api/merchant/bills',
    method: 'get',
    params: params,
    baseURL: baseURL
  });
}

/**
 * 分享关系与代理归因口径。
 *
 * 注意：这两项是 eb_ca_config 里<b>全平台共用的一份值</b>，不按商户区分 ——
 * 原平台端「淘客设置」页作废后入口挪到了商户端的淘客设置弹窗，但语义没变，
 * 任何一个商户改了，全平台所有商户的后续订单都跟着变。弹窗上必须写明这一点。
 */
function getShareRelation() {
  return (0, _request.default)({
    url: '/api/merchant/share/relation',
    method: 'get',
    baseURL: baseURL
  });
}
function saveShareRelation(data) {
  return (0, _request.default)({
    url: '/api/merchant/share/relation',
    method: 'post',
    data: data,
    baseURL: baseURL
  });
}